import { useCms } from '@eneco/cms';

import type { FlowPageContent } from '../types/page';

/**
 * The flat content of the page the flow is running on.
 *
 * The sibling of `useStepContent`: that one reads a step's own block, this one
 * reads the fields hanging off the page document itself, which is where a flow
 * keeps the values its router needs (`frontEndRootPath`, `flowName`,
 * `flowCompletionUrl`).
 *
 * `@eneco/cms` exports a `useLayoutData`, but it is not the Sitecore hook of
 * the same name and does not serve this: it returns page *context* only and
 * says so, "never component content". Page fields live on the root rendering
 * instead, already unwrapped by the adapter.
 *
 * Typed as `FlowPageContent` by default since every caller in this library
 * wants exactly that. Like `useContent`, the cast is unchecked: it is a claim
 * about the shape, not proof the shape arrived, which is why every field on
 * `FlowPageContent` is optional.
 */
export const usePageContent = <T = FlowPageContent>(): T => useCms().page.root.content as T;

/**
 * The one place `frontEndRootPath` is turned into something safe to use.
 *
 * It is typed as a string, but that is part of the unchecked cast above rather than a guarantee:
 * on Sitecore the field is a path on every page of a flow, on Sanity it is a boolean marking the
 * flow root. Every consumer goes through this, because the value fails differently depending on
 * where it lands and only one of those is obvious. Handed to the router it is a wrong route;
 * passed to `replaceAll` it is a `TypeError` that takes the whole render down, and optional
 * chaining is no defence, since `true` is neither null nor undefined.
 */
export const toFlowRootPath = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;
